import { useEffect, useRef, useState } from 'react'

import { api } from '../api'

export interface City {
  id: number
  name: string
  region: string
  country: string
  lat: number
  lon: number
  tzName: string
}

/**
 * Поиск места рождения. Место — не украшение: от долготы зависит истинное
 * солнечное время, а от часового пояса — вообще весь час рождения.
 */
export function CityPicker({
  value,
  onPick,
}: {
  value: City | null
  onPick: (city: City | null) => void
}) {
  const [query, setQuery] = useState('')
  const [items, setItems] = useState<City[]>([])
  const [open, setOpen] = useState(false)
  const [searching, setSearching] = useState(false)
  const timer = useRef<number | undefined>(undefined)

  useEffect(() => {
    window.clearTimeout(timer.current)
    const text = query.trim()
    if (text.length < 2) {
      setItems([])
      return
    }
    setSearching(true)
    timer.current = window.setTimeout(() => {
      api
        .get<{ cities: City[] }>(`/api/cities?q=${encodeURIComponent(text)}`)
        .then((data) => {
          setItems(data.cities)
          setOpen(true)
        })
        .catch(() => setItems([]))
        .finally(() => setSearching(false))
    }, 250)
    return () => window.clearTimeout(timer.current)
  }, [query])

  if (value) {
    return (
      <button
        type="button"
        className="field field-picked"
        onClick={() => {
          onPick(null)
          setQuery('')
        }}
      >
        <span className="field-value">{value.name}</span>
        <span className="field-note">{[value.region, value.country].filter(Boolean).join(' · ')}</span>
        <span className="field-change">сменить</span>
      </button>
    )
  }

  return (
    <div className="picker">
      <input
        className="field"
        type="text"
        inputMode="text"
        autoComplete="off"
        placeholder="Город рождения"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onFocus={() => setOpen(true)}
      />
      {open && query.trim().length >= 2 && (
        <div className="picker-list">
          {searching && items.length === 0 && <div className="picker-empty">ищем…</div>}
          {!searching && items.length === 0 && (
            <div className="picker-empty">
              Не нашли такой город. Попробуй ближайший крупный — расчёту важны координаты и часовой
              пояс, а они у соседних городов совпадают.
            </div>
          )}
          {items.map((city) => (
            <button
              key={city.id}
              type="button"
              className="picker-item"
              onClick={() => {
                onPick(city)
                setOpen(false)
              }}
            >
              <span className="picker-name">{city.name}</span>
              <span className="picker-note">{[city.region, city.country].filter(Boolean).join(' · ')}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
